<template>
  <div class="selector">
    <!-- Selector -->
    <div class="tab-selector" v-if="!isBForm">
      <div
        class="tab"
        v-bind:class="{ selected: isPetition }"
        v-on:click="updateSelection('petition'); logFirstTouch()"
      >
        <div class="select-img">
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3519/dist/assets/img/ra-tree-plain.svg" v-if="isPetition" />
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3519/dist/assets/img/ra-tree-hollow.svg" v-if="!isPetition" />
        </div>
        <div class="cell">
          {{ petitionSelector }}
        </div>
      </div>
      <div
        class="tab"
        v-bind:class="{ selected: !isPetition }"
        v-on:click="updateSelection('donation'); logFirstTouch()"
      >
        <div class="select-img">
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3519/dist/assets/img/ra-forest-plain.svg" v-if="!isPetition" />
          <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3519/dist/assets/img/ra-forest-hollow.svg" v-if="isPetition" />
        </div>
        <div class="cell">
          {{ donationSelector }}
        </div>
      </div>
    </div>

    <div class="petition-handle" v-if="isPetition && !isBForm">{{ petitionHandle }}</div>

    <div v-if="isDonation || isBForm">
      <!-- Dollar Handle -->
      <div class="dollar-handle-selector">
        <!-- Petition handle when using B form version -->
        <label for="handle-petition" v-if="isBForm">
          <input
            type="radio"
            v-validate="'required'"
            v-model="selectedDollarHandle"
            name="handle-petition"
            id="handle-petition"
            data-vv-as="amount"
            :value="bFormPetitionHandle"
          />
          <div class="amount">
            <span>Free</span>
          </div>
        </label>

        <label
          :for="'handle-' + currentDonationType + '-' + dollarHandle.amount"
          v-for="dollarHandle in dollarHandles"
        >
          <input
            type="radio"
            v-validate="'required'"
            v-model="selectedDollarHandle"
            :name="'handle-' + currentDonationType"
            :id="'handle-' + currentDonationType + '-' + dollarHandle.amount"
            :value="dollarHandle"
            data-vv-as="amount"
            v-on:focus="logFirstTouch()"
          />
          <div class="amount">
            <span>{{ formatDollarHandle(dollarHandle.amount) }}</span>
          </div>
        </label>

        <!-- Custom amount -->
        <label
          :for="'handle-' + currentDonationType + '-custom'"
          v-if="proposeCustomAmount"
        >
          <input
            type="radio"
            v-validate="'required'"
            v-model="selectedDollarHandle"
            :name="'handle-' + currentDonationType"
            :id="'handle-' + currentDonationType + '-custom'"
            value="custom"
            v-on:focus="logFirstTouch()"
          />
          <span class="amount">
            <span>$_</span>
          </span>
        </label>
      </div>

      <!-- Custom Amount Input -->
      <div v-if="isCustomSelected">
        <div class="wwf-field icon">
          <div class="icon"><div>$_</div></div>
          <input
            id="custom-amount"
            ref="customAmount"
            name="custom-amount"
            type="number"
            v-model="donation.amount"
            data-vv-as="custom amount"
            v-validate="{
              required: true,
              leafReliefCustomAmountIsValid: true,
            }"
            :class="{ 'border-red': errors.has('custom-amount') }"
          />
          <label :for="'handle-' + currentDonationType + '-custom'">
            Custom amount
          </label>

          <span class="wwf-alert">
            <p
              v-if="
                errors.has(
                  'custom-amount',
                  'leafReliefCustomAmountIsValid'
                )
              "
            >
              {{ errors.first("custom-amount") }}
            </p>
          </span>
        </div>
      </div>

      <!-- Message for max amount -->
      <div class="message-max-amount" v-show="showMaxAmountMessage">
        <p>
          For donations above $75,000 please contact our Impact Partnerships
          team, so we can be sure your gift benefits the area of conservation
          you are most passionate about.
        </p>
        <p>
          Please email
          <a href="mailto:partnerships@wwf.org.au" target="_blank">
            partnerships@wwf.org.au
          </a>
          . Thank you so much for your kindness!
        </p>
      </div>

      <!-- Dollar Handle Description -->
      <div class="dollar-handle-selected" v-if="isDollarHandleSelected">
        <span>{{ selectedDollarHandle.text }}</span>
      </div>

      <span class="wwf-alert">
        <p v-if="errors.first('handle-' + currentDonationType)">
          Please choose your donation amount
        </p>
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { Validator } from "vee-validate";

export default {
  name: "Selector",

  data() {
    return {
      maxAmount: 75000,
      selectedDollarHandle: null,
    };
  },
  computed: {
    ...mapGetters(["isPetition", "isDonation", "isBForm"]),
    ...mapState({
      donation: (state) => state.donation.donation,
      currentDonationType: (state) => state.donation.donation.type,
      currentDollarHandle: (state) => state.donation.donation.dollarhandle,
      product: (state) => state.product.product,
    }),
    dollarHandles() {
      return this.product.dollarhandles[this.currentDonationType];
    },
    isDollarHandleSelected() {
      return (
        this.selectedDollarHandle != null &&
        this.selectedDollarHandle != "custom" &&
        this.selectedDollarHandle != ""
      );
    },
    isCustomSelected() {
      return (
        this.selectedDollarHandle != null &&
        this.selectedDollarHandle === "custom"
      );
    },
    showMaxAmountMessage() {
      return (
        this.selectedDollarHandle === "custom" &&
        !!this.donation.amount &&
        !isNaN(this.donation.amount) &&
        +this.donation.amount > this.maxAmount
      );
    },
    minAmount() {
      if (
        !!this.product &&
        !!this.product.donationsettings &&
        !!this.currentDonationType &&
        !!this.product.donationsettings[this.currentDonationType]
      ) {
        return this.product.donationsettings[this.currentDonationType]
          .minimumAmount;
      } else {
        return null;
      }
    },
    proposeCustomAmount() {
      return !(
        this.product.extrasetting && this.product.extrasetting.hidecustomhandle
      );
    },
    currentSelection: {
      get() {
        return this.$store.state.leafRelief.selection;
      },
      set(value) {
        this.$store.commit("updateSelection", value);
      },
    },
    minAmount() {
      if (
        !!this.product &&
        !!this.product.donationsettings &&
        !!this.currentDonationType &&
        !!this.product.donationsettings[this.currentDonationType]
      ) {
        return this.product.donationsettings[this.currentDonationType]
          .minimumAmount;
      } else {
        return null;
      }
    },
    bFormPetitionHandle() {
      return { text: this.petitionHandle };
    }
  },
  props: {
    // Selectors
    petitionSelector: {
      type: String,
      default: "Plant a tree",
    },
    donationSelector: {
      type: String,
      default: "Plant a forest",
    },

    petitionHandle: {
      type: String,
      default: "Show your support and we'll plant a tree today",
    },
  },
  watch: {
    selectedDollarHandle(newVal, oldVal) {
      // For B form, change between petition and donation through dollar handle selection
      if (this.isBForm) {
        const isPetitionHandle = this.selectedDollarHandle.text === this.bFormPetitionHandle.text;
        this.updateSelection(isPetitionHandle ? 'petition' : 'donation');
      }

      if (this.isDollarHandleSelected) {
        this.donation.dollarhandle = this.selectedDollarHandle.amount;
        this.donation.amount = this.selectedDollarHandle.amount;
      } else {
        this.donation.dollarhandle = null;
        this.donation.amount = null;
        if (newVal === "custom") {
          // Give the focus to the input
          setTimeout(
            function () {
              this.$refs.customAmount.focus();
            }.bind(this),
            1
          );
        }
      }
    },
    currentDonationType(newVal) {
      // Pre select the first dollar handle
      if (this.isBForm) {
        this.selectedDollarHandle = this.bFormPetitionHandle;
        return;
      }
      if (
        !!newVal &&
        !!this.product &&
        !!this.product.dollarhandles &&
        !!this.product.dollarhandles[newVal]
      ) {
        this.selectedDollarHandle = this.product.dollarhandles[newVal][0];
      }
    },
    currentDollarHandle(newVal) {
      // Listen for the changes from the widget
      if (!!this.dollarHandles) {
        const dh = this.dollarHandles.find((e) => e.amount === newVal);
        if (!!dh) {
          this.selectedDollarHandle = dh;
        }
      }
    },
  },

  created() {
    // Custom validation for amount (regular/single)
    let leafReliefCustomAmountIsValid = (amount) => {
      if (amount <= 0) {
        return {
          valid: false,
          data: { message: "The amount must be positive." },
        };
      }

      // Get the min amount from the settings of the selected type or the default one if not set
      const minimumAmount = !!this.minAmount
        ? parseFloat(this.minAmount)
        : this.type === "Regular"
        ? 15
        : 2;

      const inputAmount = parseInt(amount.replace(/ /g, ""));
      if (inputAmount < minimumAmount) {
        return {
          valid: false,
          data: { message: "The minimum amount is $" + minimumAmount },
        };
      } else if (inputAmount > this.maxAmount) {
        return {
          valid: false,
          data: { message: "The maximum amount is $75,000" },
        };
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("leafReliefCustomAmountIsValid", {
      validate: leafReliefCustomAmountIsValid,
      getMessage: (field, params, data) => data.message,
    });
  },
  mounted() {
    // Pre select the first dollar handle
    if (
      !!this.product &&
      !!this.product.dollarhandles &&
      !!this.product.dollarhandles.Single
    ) {
      this.selectedDollarHandle = this.product.dollarhandles.Single[0];
    }
  },
  methods: {
    updateSelection(select) {
      this.currentSelection = select;
    },
    formatDollarHandle(handle) {
      return `$${handle.indexOf(".00") >= 0 ? handle.slice(0, -3) : handle}`;
    },
    validate() {
      return new Promise((resolve, reject) => {
        this.$validator
          .validateAll()
          .then((result) => {
            // Will return true if valid and false if not
            if (result) {
              resolve(result);
            } else {
              console.error("Form Selection not valid - result", result);
              reject(result);
            }
          })
          .catch((err) => {
            console.error("Step 1 Validation - Error - ", err);
            reject(err);
          });
      });
    },
    commit() {
      // Add .00 if no . and add 0 if no 0 (eg 42.6)
      this.donation.amount =
        this.donation.amount.indexOf(".") == -1
          ? this.donation.amount.concat(".00")
          : /^\d+(\.\d{1})+$/.test(this.donation.amount)
          ? this.donation.amount.concat("0")
          : this.donation.amount;
    },
    logFirstTouch() {
      this.$emit("firstTouch");
    }
  },
};
</script>

<style scoped>
.selector {
  margin-bottom: 2rem;
}

.selector .tab-selector {
  margin-bottom: 0.5rem;
}

.selector .tab-selector .tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  flex: 1;
  cursor: pointer;
}

.selector .tab-selector .tab .select-img {
  height: 80px;
}

.selector .tab-selector .tab .cell {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.7rem;
  width: 100%;
  font-family: var(--wwfFont);
  font-size: 24px;
  text-align: center;
  text-transform: unset;
  color: var(--wwfRegenerateAustralia);
  background-color: var(--wwfWhite);
  border: 2px solid var(--wwfRegenerateAustralia);
  letter-spacing: 1px;
}

.selector .tab-selector .tab.selected .cell {
  color: var(--wwfWhite);
  background-color: var(--wwfRegenerateAustralia);
}

.petition-handle {
  color: var(--wwfRegenerateAustralia);
  font-size: 16px;
  line-height: 18px;
}

.dollar-handle-selector label {
  color: var(--wwfRegenerateAustralia);
  border: 2px solid var(--wwfRegenerateAustralia);
  border-right: none;
}

.dollar-handle-selector label:last-child {
  border-right: 2px solid var(--wwfRegenerateAustralia);
}

.dollar-handle-selector label input:checked + .amount {
  color: var(--wwfWhite);
  background-color: var(--wwfRegenerateAustralia);
}

.dollar-handle-selector label input:checked + .amount::after {
  background-color: var(--wwfRegenerateAustralia);
}

.dollar-handle-selected {
  color: var(--wwfRegenerateAustralia);
}

.message-max-amount a {
  color: var(--wwfRegenerateAustralia);
}

span.wwf-alert p {
  top: -29px;
}
</style>


// WEBPACK FOOTER //
// Selector.vue?d4f91b1a